import * as React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import * as Components from '../../components/index';
import * as Models from '../../models/index';
import * as ActionCreators from '../../actions/action.creators';
import * as PwdSelectors from './selectors/password.selector';
import classnames from 'classnames';
import _ from 'lodash';

import './index.scss';

interface StateProps {
	oldPassword: string;
	password: string;
	confirmPassword: string;
	username: string;
	processing: boolean;
	pwdValidationResult: Models.ValidateUserPassword_Response_Client[];
	isNeededToChangePWD: boolean;
	secretQuestions: Models.CheckSecretAnswersByUserName_Response_Client[];
	visibleSecretQuestions: boolean;
	secretAnswers: Models.SecretAnswer[];
	validateAnswers: boolean;
}
interface DispatchProps {
	changeOldPwd(value: string): any;
	validateUserPassword(isExpired?: boolean): any;
	validateOldPassword(): any;
	changePassword(value: string, isPwd: boolean, isExpired: boolean): any;
	resetPwd(): any;
	setDefault(): any;
	changeUserPassword(): any;
	checkResetAnswersByUserName(): any;
	showSecretQuestions: () => void;
	updateSecretAnswers: (questionId: string, answerId: string) => void;
	changeSecretAnswerText: (answerId: string, answerText: string) => void;
	changePasswordAndSubmitSecretAnswers: () => void;
}
const mapStateToProps = (state: Models.StoreState): StateProps => ({
	confirmPassword: state.pwdSecurity.confirmPassword,
	oldPassword: state.pwdSecurity.oldPassword,
	password: state.pwdSecurity.password,
	username: state.pwdRecovery.username,
	processing: state.shell.processing,
	pwdValidationResult: PwdSelectors.validateResult(state),
	isNeededToChangePWD: state.login.isNeededToChangePWD,
	secretQuestions: state.pwdSecurity.secretQuestions,
	visibleSecretQuestions: state.pwdSecurity.visibleSecretQuestions,
	secretAnswers: state.pwdSecurity.answers,
	validateAnswers: PwdSelectors.validateSecretAnswers(state),
});
const mapDispatchToProps = (dispatch: any): DispatchProps => ({
	changeOldPwd: (value: string) => dispatch(ActionCreators.PwdSecurity.changeOldPassword(value)),
	resetPwd: () => dispatch(ActionCreators.PwdExpired.resetPassword()),
	validateUserPassword: isExpired => dispatch(ActionCreators.PwdValidate.validateUserPassword(isExpired)),
	validateOldPassword: () => dispatch(ActionCreators.PwdValidate.validateOldPassword()),
	changePassword: (value, isPwd, isExpired) => dispatch(ActionCreators.PwdSecurity.changePassword(value, isPwd, isExpired)),
	setDefault: () => dispatch(ActionCreators.PwdSecurity.setDefault()),
	changeUserPassword: () => dispatch(ActionCreators.PwdChange.changeUserPassword()),
	checkResetAnswersByUserName: () => dispatch(ActionCreators.PwdSecurity.checkSecretAnswersByUserName()),
	showSecretQuestions: () => dispatch(ActionCreators.PwdSecurity.showSecretQuestions()),
	updateSecretAnswers: (questionId, answerId) => dispatch(ActionCreators.PwdSecurity.updateSecretAnswers(questionId, answerId)),
	changeSecretAnswerText: (answerId, answerText) => dispatch(ActionCreators.PwdSecurity.changeSecretAnswerText(answerId, answerText)),
	changePasswordAndSubmitSecretAnswers: () => dispatch(ActionCreators.PwdSecurity.changePasswordAndSubmitSecretAnswers()),
});

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(
	class Component extends React.Component<StateProps & DispatchProps, any> {
		componentDidMount() {
			this.props.validateUserPassword(true);
			this.props.validateOldPassword();
			this.props.checkResetAnswersByUserName();
		}

		componentWillUnmount() {
			this.props.setDefault();
		}

		changeOrResetPWD = () => {
			this.props.isNeededToChangePWD ? this.props.changeUserPassword() : this.props.resetPwd();
		};

		renderTitle = () => {
			if (this.props.visibleSecretQuestions) return 'PICK YOUR SECRET QUESTIONS';
			return this.props.isNeededToChangePWD ? 'Change Password' : 'Reset Password';
		};

		renderInformSection = () => {
			if (this.props.visibleSecretQuestions) {
				return 'To help provide a method to help unlock or reset your Tracks password please choose three questions from the list below and provide your personal response.';
			}

			const informText = this.props.isNeededToChangePWD
				? 'You have to change your password to continue.'
				: 'Your password has expired, you must reset to continue.';

			return (
				<div>
					{informText}
					<div className='ep-important-note'>*Please take note of your new password.</div>
				</div>
			);
		};

		renderFooterButton = (allTestPassed: boolean) => {
			if (this.props.visibleSecretQuestions) {
				return (
					<Components.Button
						busy={this.props.processing}
						className={classnames({ disabled: !this.props.validateAnswers })}
						onClick={this.props.changePasswordAndSubmitSecretAnswers}
					>
						Submit
					</Components.Button>
				);
			}

			return (
				<Components.Button
					className={classnames({ disabled: !allTestPassed })}
					onClick={this.props.secretQuestions.length !== 0 ? this.props.showSecretQuestions : this.changeOrResetPWD}
				>
					{this.props.isNeededToChangePWD ? 'Change' : 'Reset'}
				</Components.Button>
			);
		};

		filterQuestions = (currentAnswerId: string) => {
			let answersWithoutCurrent = this.props.secretAnswers.filter(el => el.answerId !== currentAnswerId);
			return _.filter(this.props.secretQuestions, question => !_.some(answersWithoutCurrent, el => el.questionId === question.questionId)).map(
				x => ({ itemText: x.questionText, itemValue: x.questionId })
			);
		};

		renderBody = (testResults: Models.ValidateUserPassword_Response_Client[]) => {
			if (this.props.visibleSecretQuestions) {
				return (
					<Components.LayoutBlock className='ep-answer'>
						{this.props.secretAnswers.map((answer, index) => {
							let questions = this.filterQuestions(answer.answerId);
							return (
								<Components.LayoutBlock key={answer.answerId}>
									<Components.DropDown
										label={'Question ' + (index + 1)}
										validate={!!answer.answerText && !!answer.questionId}
										options={questions}
										onChange={value => {
											this.props.updateSecretAnswers(value, answer.answerId);
										}}
									/>
									<Components.Textbox
										key={answer.answerId + index}
										className='ep-answer-input'
										placeholder='Answer'
										type='password'
										value={answer.answerText}
										onChange={value => this.props.changeSecretAnswerText(answer.answerId, value.trim())}
									/>
								</Components.LayoutBlock>
							);
						})}
					</Components.LayoutBlock>
				);
			}

			return (
				<>
					<Components.LayoutBlock>
						<Components.Textbox
							placeholder='Old Password'
							type='password'
							value={this.props.oldPassword}
							onChange={this.props.changeOldPwd.bind(this)}
							onChanged={() => this.props.validateOldPassword()}
						/>
						<Components.Textbox
							maxLength={128}
							placeholder='New Password'
							type='password'
							value={this.props.password}
							onChange={value => this.props.changePassword(value, true, true)}
							onChanged={() => this.props.validateUserPassword(true)}
						/>
						<Components.Textbox
							maxLength={128}
							placeholder='Confirm'
							type='password'
							value={this.props.confirmPassword}
							onChange={value => this.props.changePassword(value, false, true)}
							onChanged={() => this.props.validateUserPassword(true)}
						/>
					</Components.LayoutBlock>
					<Components.ListBox
						height='175px'
						name='Please create a password that meets the following requirements'
						items={testResults}
						listItemStyle='text-result'
					/>
				</>
			);
		};

		render() {
			if (this.props.username === '') return <Redirect to='/' />;

			var testResults = this.props.pwdValidationResult;
			var allTestPassed = testResults.filter(x => !x.isPassed).length === 0;

			return (
				<Components.LayoutBlock verticalAlign='center' align='center' className='view expired-password'>
					<Components.LayoutBlock fill style={{ alignSelf: 'stretch' }}>
						<Components.LayoutBlock inline fill className='header'>
							{this.renderTitle()}
						</Components.LayoutBlock>
						<Components.LayoutBlock fill>
							<Components.LayoutBlock fill className='password-form'>
								<Components.LayoutBlock inline={true} verticalAlign='center' className='inform-section'>
									<Components.Icon icon='clock' size='4' />
									<Components.Label className='reset-label'>{this.renderInformSection()}</Components.Label>
								</Components.LayoutBlock>
								{this.renderBody(testResults)}
								<Components.LayoutBlock className='ep-footer-buttons'>
									{this.renderFooterButton(allTestPassed)}
								</Components.LayoutBlock>
							</Components.LayoutBlock>
						</Components.LayoutBlock>
					</Components.LayoutBlock>
				</Components.LayoutBlock>
			);
		}
	}
);
